import React from "react";
import { alpha, Chip, Container, Skeleton, Typography } from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { DataGrid } from "@mui/x-data-grid";
import TicketsTableActions from "../TicketsTableActions/index.js";
import TicketsStatusActions from "../TicketsStatusActions/index.js";
import styles from "../TicketsTable/ticketsTableStyles.js";
import { RecordCircle } from "iconsax-react";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

function StatusChip({ status }) {
  const statusConfig = {
    "Pendiente": { color: "#FFA726", label: "Pendiente" },
    "En Proceso": { color: "#FFA726", label: "En Proceso" },
    "Procesando": { color: "#FFA726", label: "Procesando" },
    "procesando": { color: "#FFA726", label: "Procesando" },
    "Terminado": { color: "#53CA98", label: "Terminado" },
    "terminado": { color: "#53CA98", label: "Terminado" },
    "registrado": { color: "#42A5F5", label: "Registrado" },
    "Resuelto": { color: "#53CA98", label: "Resuelto" },
    "Cerrado": { color: "#78909C", label: "Cerrado" },
    "Cancelado": { color: "#DB463C", label: "Cancelado" },
  };

  const config = statusConfig[status] || { color: "#78909C", label: status || "N/A" };

  return (
    <Chip
      sx={{
        color: config.color,
        backgroundColor: alpha(config.color, 0.1),
      }}
      icon={<RecordCircle size="12" variant="Bold" />}
      label={config.label}
      size="small"
    />
  );
}

const TicketsTableRegistrado = ({ tickets = [], search, isLoading, fetchTickets, currentStatus }) => {
  const { hasAllPermissions } = usePermissions();
  
  const hasStatusActionsPermission = hasAllPermissions(["tkt_vw", "tkt_ovw", "tkt_ups"]);

  // Columnas personalizables para el tab Registrado
  const columns = [
    {
      field: "id",
      headerName: "REPORTE",
      minWidth: 100,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>REPORTE</strong>,
    },
    {
      field: "guideNumber",
      headerName: "REPORTE",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NÚMERO DE GUÍA</strong>,
    },
    {
      field: "reportType",
      headerName: "TIPO DE REPORTE",
      minWidth: 150,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>TIPO DE REPORTE</strong>,
    },
    {
      field: "client",
      headerName: "CLIENTE",
      minWidth: 160,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>CLIENTE</strong>,
    },
    {
      field: "reportDate",
      headerName: "FECHA DE REPORTE",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>FECHA DE REPORTE</strong>,
    },
    /* {
      field: "lastUpdateDate",
      headerName: "ÚLTIMA ACTUALIZACIÓN",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ÚLTIMA ACTUALIZACIÓN</strong>,
    }, */
    {
      field: "status",
      headerName: "STATUS",
      width: 130,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>STATUS</strong>,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    ...(hasStatusActionsPermission ? [{
      field: "statusActions",
      headerName: "STATUS",
      type: "string",
      width: 170,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>CAMBIAR STATUS</strong>,
      renderCell: (params) => <TicketsStatusActions ticket={params.row} fetchTickets={fetchTickets} currentStatus={currentStatus} />,
    }] : []),
    {
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "center",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => <TicketsTableActions ticket={params.row} fetchTickets={fetchTickets} />,
    },
  ];

  const filteredRows = tickets.filter((ticket) => {
    const transformedTicket = {
      ...ticket,
      client: `${ticket.clientName} - ${ticket.clientId}`,
    };
    return Object.values(transformedTicket).some((value) =>
      value !== null && value !== undefined && value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  // Función para capitalizar la primera letra
  const capitalizeFirstLetter = (str) => {
    if (!str || str === "N/A") return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const rows = filteredRows?.map((ticket) => ({
    id: ticket.report_id || ticket.id,
    reportNumber: ticket.report_id,
    guideNumber: ticket.guideNumber,
    userType: ticket.userType,
    provider: ticket.provider ? capitalizeFirstLetter(ticket.provider) : "N/A",
    reportType: ticket.reportType,
    client: ticket.clientName || "N/A",
    clientName: ticket.clientName,
    clientId: ticket.clientId,
    clientEmail: ticket.clientEmail,
    reportDate: ticket.reportDate,
    lastUpdateDate: ticket.lastUpdateDate || "N/A",
    status: ticket.status,
    administratorResponse: ticket.administratorResponse,
    images: ticket.images || {},
    address: ticket.address || {},
    package_info: ticket.package_info || {},
  }));

  return (
    <Container sx={styles.mainContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'12%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'12%'} height={40} />
            <Skeleton variant="rounded" width={'15%'} height={40} />
            <Skeleton variant="rounded" width={'12%'} height={40} />
            <Skeleton variant="rounded" width={'12%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'8%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'12%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'12%'} height={300} />
            <Skeleton variant="rounded" width={'15%'} height={300} />
            <Skeleton variant="rounded" width={'12%'} height={300} />
            <Skeleton variant="rounded" width={'12%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'8%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowHeight={() => 'auto'} 
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { 
              py: '8px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { 
              py: '0px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { 
              py: '22px', 
              display: 'flex', 
              alignItems: 'center' 
            },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          localeText={{ noRowsLabel: <NoResultsComponent /> }}
        />
      )}
    </Container>
  );
};

const NoResultsComponent = () => (
  <Container sx={styles.noDataContainer}>
    <SearchOffIcon sx={styles.noDataIcon} />
    <Typography sx={styles.noDataTitle}>No se encontraron tickets</Typography>
  </Container>
);

export default TicketsTableRegistrado;

